import { makeEnum, makeOptions,
  setupClass, lookupData, 
  interpolateInMap,
} from '../Base.js'
import { InputComponent } from '../Common/InputComponent.js'

import { Units } from '../Common/Units.js'

import { prettyJson, valOr,
  addElem, removeElem, elemIn,
  extendMap,
  downloadTextFile,
  generateItemName,
  sortItemsByName,
  formatNum,
} from '../SharedUtils.js'

import {
  FieldType,
  Field,
  FieldGroup,
} from '../Common/Field.js'

import {
  kDirectionChoices, YesNo,
  makeYesNoField,
 } from './Common.js'

 import {
  NumPanes,
  FrameStyle,
  Glazing,
  PanesAppliedTo,
  Tint,
  PaneThickness,
  GapType,
 } from './WindowsCommon.js'

 import {
  WindowType
 } from './WindowType.js'


import { gApp, DebugOn } from '../Globals.js'

export let SkylightStyle = makeEnum({
  ManufacturedSkylight: 'Manufactured skylight',
  SiteAssembledSlopedSlashOverheadGlazing: 'Site-assembled sloped/overhead glazing',
  SlopedCurtainWall: 'Sloped curtain wall (> 20° from horizontal)',
  DomedSkylight: 'Domed skylight',
  TubularDaylightingDevice: 'Tubular daylighting device',
})

export let SkylightInputType = makeEnum({
  Manual: 'Manual',
  BuildSkylight: 'Build skylight',
})

let DomedSkylightOpacity = makeEnum({
  Clear: 'Clear',
  ClearWithLightDiffuser: 'Clear with light diffuser',
  MildyTranslucent: 'Mildy translucent',
  VeryTranslucent: 'Very translucent',
})

let DomedSkylightGlazing = makeEnum({
  Single: 'Single',
  Double: 'Double',
  DoubleLowE: 'Double, low-e',
  Triple: 'Triple',
  TripleLowE: 'Triple, low-e',
})

let DomedSkylightUValueLookupRows = {
  [DomedSkylightGlazing.Single]: 2,
  [DomedSkylightGlazing.Double]: 6,
  [DomedSkylightGlazing.DoubleLowE]: 23,
  [DomedSkylightGlazing.Triple]: 36,
  [DomedSkylightGlazing.TripleLowE]: 48,
}

let DomedSkylightShgcs = {
  [DomedSkylightOpacity.ClearWithLightDiffuser]: {
    [0]: 0.53,
    [9]: 0.5,
    [12]: 0.44,
  },
  [DomedSkylightOpacity.Clear]: {
    [0]: 0.86,
    [9]: 0.77,
    [12]: 0.7,
  },
  [DomedSkylightOpacity.MildyTranslucent]: {
    [0]: 0.5,
    [12]: 0.4,
  },
  [DomedSkylightOpacity.VeryTranslucent]: {
    [0]: 0.3,
    [9]: 0.26,
    [12]: 0.24,
  }
};

let TubularSkylightCollectorLayers = makeEnum({
  N1: '1',
  N2: '2',
})

let TubularSkylightDiffuserLayers = makeEnum({
  N1: '1',
  N2: '2',
  N3: '3',
})

let RoofInsulationLocation = makeEnum({
  BelowRoof: 'Below roof',
  AboveCeiling: 'Above ceiling',
})

let TubularSkylightData = {
  [TubularSkylightCollectorLayers.N1]: {
    [TubularSkylightDiffuserLayers.N1]: {
      [RoofInsulationLocation.AboveCeiling]: {uValue: 0.62, shgc: 0.32},
      [RoofInsulationLocation.BelowRoof]: {uValue: 1.34, shgc: 0.34},
    },
    [TubularSkylightDiffuserLayers.N2]: {
      [RoofInsulationLocation.AboveCeiling]: {uValue: 0.38, shgc: 0.28},
      [RoofInsulationLocation.BelowRoof]: {uValue: 1.34, shgc: 0.35},
    },
    [TubularSkylightDiffuserLayers.N3]: {
      [RoofInsulationLocation.AboveCeiling]: {uValue: 0.27, shgc: 0.24},
      [RoofInsulationLocation.BelowRoof]: {uValue: 1.33, shgc: 0.34},
    },
  },
  [TubularSkylightCollectorLayers.N2]: {
    [TubularSkylightDiffuserLayers.N1]: {
      [RoofInsulationLocation.AboveCeiling]: {uValue: 0.62, shgc: 0.27},
      [RoofInsulationLocation.BelowRoof]: {uValue: 0.83, shgc: 0.32},
    },
    [TubularSkylightDiffuserLayers.N2]: {
      [RoofInsulationLocation.AboveCeiling]: {uValue: 0.38, shgc: 0.24},
      [RoofInsulationLocation.BelowRoof]: {uValue: 0.83, shgc: 0.38},
    },
    [TubularSkylightDiffuserLayers.N3]: {
      [RoofInsulationLocation.AboveCeiling]: {uValue: 0.27, shgc: 0.24},
      [RoofInsulationLocation.BelowRoof]: {uValue: 1.33, shgc: 0.34},
    },
  },
}

export class SkylightType extends InputComponent {
  init(name, makeId) {
    this.id = makeId ? gApp.proj().makeId('SkylightType') : 0;

    this.name = Field.makeName('Name', name)
    this.height = new Field({
      name: 'Length',
      type: FieldType.Length,
      requiresInput: true,
      note: 'The length of the skylight opening.',
    })
    this.width = new Field({
      name: 'Width',
      type: FieldType.Length,
      requiresInput: true,
      note: 'The width of the skylight opening.',
    })
    this.curbHeight = new Field({
      name: 'Curb Height',
      type: FieldType.SmallLength,
      min: 0,
      allowMin: true,
      note: 'The height of the curb (the raised opaque frame around the skylight). Forced to 0 for sloped curtain wall style.',
    })
    this.style = Field.makeSelect('Style', SkylightStyle, {
      note: 'The construction style of the skylight. The remaining fields depend on this selection.',
    })
    this.inputType = Field.makeSelect('Input mode', SkylightInputType, {
      bold: true,
      note: 'The skylight U-value and SHGC may either be entered manually or calculated by building a skylight using the skylight builder.',
    })

    this.curbHeight.makeUpdater((field) => {
      if (this.style.value == SkylightStyle.SlopedCurtainWall) {
        field.isOutput = true;
        field.value = 0;
      } else {
        field.isOutput = false;
      }
    })

    this.manualUValue = new Field({
      name: 'U-value',
      type: FieldType.UValue,
      min: 0,
      allowMin: false,
      requiresInput: true,
      note: 'The U-value of the skylight. Only relevant when input mode is set to Manual.',
    })
    this.manualShgc = new Field({
      name: 'SHGC (total window)',
      type: FieldType.Ratio,
      requiresInput: true,
      note: 'The total-window SHGC of the skylight. Only relevant when input mode is set to Manual.',
    })
    this.manualInputFields = ['manualUValue', 'manualShgc']
    this.updater.setFieldsEnabled('manual', this, this.manualInputFields, () => {
      return this.inputType.value == SkylightInputType.Manual;
    })

    this.domedSkylightInputs = new FieldGroup([
      Field.makeSelect('Dome Opacity', DomedSkylightOpacity, {
        key: 'domeOpacity',
        note: 'The opacity of the dome. More translucent domes admit less solar gain.',
      }),
      new Field({
        key: 'frameStyle',
        name: 'Frame Style',
        type: FieldType.Select,
        choices: makeOptions(FrameStyle, [
          FrameStyle.AluminumWithoutThermalBreak,
          FrameStyle.AluminumWithThermalBreak,
          FrameStyle.ReinforcedVinylSlashAluminumCladWood,
          FrameStyle.WoodSlashVinyl,
        ]),
        note: 'The frame style of the domed skylight.',
      }),
      Field.makeSelect('Glazing', DomedSkylightGlazing, {
        key: 'glazing',
        note: 'The glazing layers of the dome. Low-e options improve thermal performance.',
      }),
    ])
    this.domedSkylightInputs.setVisibility(() => {
      return this.inputType.value == SkylightInputType.BuildSkylight &&
        this.style.value == SkylightStyle.DomedSkylight;
    })

    this.tubularSkylightInputs = new FieldGroup([
      Field.makeSelect('# of glazing layers on collector',
        TubularSkylightCollectorLayers, {
        key: 'collectorLayers',
        note: 'The number of glazing layers on the collector (the dome on the roof).',
      }),
      Field.makeSelect('# of glazing layers on diffuser',
        TubularSkylightDiffuserLayers, {
        key: 'diffuserLayers',
        note: 'The number of glazing layers on the diffuser (the lens at the ceiling). Only one layer will result in poor thermal performance.',
      }),
      Field.makeSelect('Approximate location of roof insulation',
        RoofInsulationLocation, {
        key: 'insulationLocation',
        note: 'Where the roof insulation is located relative to the tubular skylight. Thermal performance is greatly improved when the insulation is above the ceiling. If unknown, select "Below roof".',
      }),
    ])
    this.tubularSkylightInputs.setVisibility(() => {
      return this.inputType.value == SkylightInputType.BuildSkylight &&
        this.style.value == SkylightStyle.TubularDaylightingDevice;
    })
    
    // Special styles - these styles have different inputs than the others.
    let isSpecialStyle = () => {
      return elemIn(this.style.value, [
        SkylightStyle.DomedSkylight,
        SkylightStyle.TubularDaylightingDevice
      ])
    };
    this.frameStyle = new Field({
      name: 'Frame Style',
      type: FieldType.Select,
      choices: makeOptions(FrameStyle, [
        FrameStyle.AluminumWithoutThermalBreak,
        FrameStyle.AluminumWithThermalBreak,
        FrameStyle.ReinforcedVinylSlashAluminumCladWood,
        FrameStyle.WoodSlashVinyl,
      ]),
      note: 'The frame style of the skylight. The available options depend on the skylight style.',
      notesData: {
        'AllOptions': makeOptions(FrameStyle),
      },
    })
    this.frameStyle.makeChoicesUpdater((field) => {
      if (isSpecialStyle()) {
        return [];
      }
      if (this.style.value == SkylightStyle.ManufacturedSkylight) {
        return makeOptions(FrameStyle, [
          FrameStyle.AluminumWithoutThermalBreak,
          FrameStyle.AluminumWithThermalBreak,
          FrameStyle.ReinforcedVinylSlashAluminumCladWood,
          FrameStyle.WoodSlashVinyl,
        ])
      } else if (this.style.value == SkylightStyle.SiteAssembledSlopedSlashOverheadGlazing) {
        return makeOptions(FrameStyle, [
          FrameStyle.AluminumWithoutThermalBreak,
          FrameStyle.AluminumWithThermalBreak,
          FrameStyle.StructuralGlazing,
        ])
      } else if (this.style.value == SkylightStyle.SlopedCurtainWall) {
        return makeOptions(FrameStyle, [
          FrameStyle.AluminumWithoutThermalBreak,
          FrameStyle.AluminumWithThermalBreak,
          FrameStyle.StructuralGlazing,
        ])
      }
    })
    this.useDefaultFrameWidth = Field.makeSelect('Use default frame width', YesNo, {
      note: 'If Yes, the frame width is calculated automatically from the skylight and frame style. If No, you can enter a custom frame width.',
    })
    this.frameWidth = new Field({
      name: 'Frame width',
      type: FieldType.SmallLength,
      note: 'The width of the skylight frame. Calculated automatically when "Use default frame width" is Yes, otherwise entered manually.',
    })
    this.frameWidth.makeUpdater((field) => {
      if (isSpecialStyle()) {
        return;
      }
      if (this.useDefaultFrameWidth.value == YesNo.Yes) {
        field.isOutput = true;
        let frameWidth = lookupData(WindowType._getStdFrameWidthMap(), [
          this.style.value,
          this.frameStyle.value,
        ])
        field.setValueInUnits(frameWidth, Units.inches);
      } else {
        field.isOutput = false;
      }
    })
    this.numPanes = Field.makeSelect('Number of panes', NumPanes, {
      note: 'The number of panes of glass in the skylight.',
    })
    this.glazing = Field.makeSelect('Glazing', Glazing, {
      note: 'The skylight glazing/coating. The available options depend on the number of panes.',
      notesData: {
        'AllOptions': makeOptions(Glazing),
      },
    })
    this.glazing.makeChoicesUpdater(() => {
      if (isSpecialStyle()) {
        return [];
      }
      if (this.numPanes.value == NumPanes.N1) {
        return makeOptions(Glazing, [Glazing.None])
      } else if (this.numPanes.value == NumPanes.N2) {
        return makeOptions(Glazing)
      } else {
        return makeOptions(Glazing, [
          Glazing.None,
          Glazing.E0_2,
          Glazing.E0_1,
          Glazing.E0_05,
        ])
      }
    })
    this.panesAppliedTo = Field.makeSelect('Panes applied to', PanesAppliedTo, {
      note: 'For multi-pane skylights, which panes the glazing/coating is applied to. The available options depend on the number of panes and the glazing.',
      notesData: {
        'AllOptions': makeOptions(PanesAppliedTo),
      },
    })
    this.panesAppliedTo.makeChoicesUpdater((field) => {
      if (isSpecialStyle()) {
        return [];
      }
      if (this.numPanes.value == NumPanes.N1) {
        return [];
      } else if (this.numPanes.value == NumPanes.N2) {
        if (this.glazing.value == Glazing.None) {
          return [];
        } else if (elemIn(this.glazing.value, [Glazing.E0_6])) {
          return makeOptions(PanesAppliedTo, [PanesAppliedTo.Surface2or3])
        } else {
          return makeOptions(PanesAppliedTo, [
            PanesAppliedTo.Surface2,
            PanesAppliedTo.Surface3,
          ])
        }
      } else {
        if (this.glazing.value == Glazing.None) {
          return [];
        } else if (this.glazing.value == Glazing.E0_2) {
          return makeOptions(PanesAppliedTo, [
            PanesAppliedTo.Surface2or3,
            PanesAppliedTo.Surface4or5,
            PanesAppliedTo.Surface2or3and4or5,
          ])
        } else {
          return makeOptions(PanesAppliedTo, [
            PanesAppliedTo.Surface2or3and4or5
          ])
        }
      }
    })
    this.tint = Field.makeSelect('Tint / reflective coating', Tint, {
      note: 'The tint or reflective coating on the skylight panes. The available options depend on the number of panes, the glazing, and the panes-applied-to selection.',
      notesData: {
        'AllOptions': makeOptions(Tint),
      },
    })
    this.tint.makeChoicesUpdater(() => {
      if (isSpecialStyle()) {
        return []
      }
      if (this.numPanes.value == NumPanes.N1) {
        return makeOptions(Tint, [
          Tint.Clear,
          Tint.Bronze,
          Tint.Green,
          Tint.Grey,
          Tint.BlueGreen,
          Tint.StainlessSteelOnClear8p,
          Tint.StainlessSteelOnClear14p,
          Tint.StainlessSteelOnGreen14p,
          Tint.StainlessSteelOnClear20p,
          Tint.TitaniumOnClear20p,
          Tint.TitaniumOnClear30p,
        ])
      } else if (this.numPanes.value == NumPanes.N2) {
        if (elemIn(this.glazing.value, [Glazing.None, Glazing.E0_6])) {
          return makeOptions(Tint)
        } else if (elemIn(this.glazing.value, [Glazing.E0_4, Glazing.E0_2, Glazing.E0_1])) {
          if (this.panesAppliedTo.value == PanesAppliedTo.Surface2) {
            return makeOptions(Tint, [Tint.Clear])
          } else {
            return makeOptions(Tint, [
              Tint.Clear,
              Tint.Bronze,
              Tint.Green,
              Tint.Grey,
              Tint.BlueGreen,
              Tint.HighPerfGreen,
            ])
          }
        } else if (this.glazing.value == Glazing.E0_05) {
          return makeOptions(Tint, [
            Tint.Clear,
            Tint.Bronze,
            Tint.Green,
            Tint.Grey,
            Tint.BlueGreen,
            Tint.HighPerfGreen,
          ])
        }
      } else {
        if (this.glazing.value == Glazing.None) {
          return makeOptions(Tint, [Tint.Clear, Tint.HighPerfGreen])
        } else {
          return makeOptions(Tint, [Tint.Clear])
        }
      }
    })
    this.paneThickness = Field.makeSelect('Pane thickness', PaneThickness, {
      note: 'The thickness of the skylight panes. The available options depend on the tint.',
      notesData: {
        'AllOptions': makeOptions(PaneThickness),
      },
    })
    this.paneThickness.makeChoicesUpdater(() => {
      if (isSpecialStyle()) {
        return []
      }
      if (elemIn(this.tint.value, [Tint.Clear, Tint.Bronze, Tint.Green, Tint.Grey])) {
        return makeOptions(PaneThickness)
      } else {
        return makeOptions(PaneThickness, [PaneThickness.QuarterInch])
      }
    })
    this.gapType = Field.makeSelect('Gap type', GapType, {
      note: 'For multi-pane skylights, the type of gap between the panes. The available options depend on the number of panes.',
      notesData: {
        'AllOptions': makeOptions(GapType),
      },
    })
    this.gapType.makeChoicesUpdater(() => {
      if (isSpecialStyle()) {
        return [];
      }
      if (this.numPanes.value == NumPanes.N1) {
        return makeOptions(GapType, [GapType.None])
      } else if (elemIn(this.numPanes.value, [NumPanes.N2, NumPanes.N3])) {
        return makeOptions(GapType, [
          GapType.QuarterInchAir,
          GapType.HalfInchAir,
          GapType.QuarterInchArgon,
          GapType.HalfInchArgon,
        ])
      } else {
        return makeOptions(GapType, [
          GapType.QuarterInchAir,
          GapType.HalfInchAir,
          GapType.QuarterInchArgon,
          GapType.HalfInchArgon,
          GapType.QuarterInchKrypton,
        ])
      }
    })
    this.builderFields = [
      'frameStyle',
      'useDefaultFrameWidth',
      'frameWidth',
      'numPanes',
      'glazing',
      'panesAppliedTo',
      'tint',
      'paneThickness',
      'gapType',
    ]
    this.updater.setFieldsEnabled('builder-fields', this, this.builderFields, () => {
      return this.inputType.value == SkylightInputType.BuildSkylight && !isSpecialStyle();
    })
    // }

    this.outputUValue = new Field({
      name: 'Output U-value',
      type: FieldType.UValue,
      isOutput: true,
      allowMin: false,
      note: 'The U-value of the skylight, calculated from your inputs.',
    })
    this.outputUValue.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.inputType.value == SkylightInputType.Manual) {
        field.waitingInputs = Field.checkWaitingInputs([this.manualUValue])
      } else {
        field.waitingInputs = Field.checkWaitingInputs([
          this.height, this.width,
        ])
      }
      let uValue = this.computeUValue();
      field.setValueInUnits(uValue.uValue, Units.UValue);
      field.debugOutput = DebugOn() ? prettyJson(uValue) : null;
    })
    this.outputShgc = new Field({
      name: 'Output SHGC',
      type: FieldType.Ratio,
      isOutput: true,
      allowMin: false,
      note: 'The total-window SHGC of the skylight, calculated from your inputs.',
    })
    this.outputShgc.makeUpdater((field) => {
      field.waitingInputs = false;
      if (this.inputType.value == SkylightInputType.Manual) {
        field.waitingInputs = Field.checkWaitingInputs([this.manualShgc])
      } else {
        field.waitingInputs = Field.checkWaitingInputs([
          this.height, this.width,
        ])
      }
      let shgcs = this.computeShgc();
      field.value = shgcs.shgc;
      field.debugOutput = DebugOn() ? prettyJson(shgcs) : null;
    })

    this.serFields = [
      'id',
      'name',
      'height',
      'width',
      'curbHeight',
      'style',
      'inputType',
      'manualUValue',
      'manualShgc',
      'domedSkylightInputs',
      'tubularSkylightInputs',
      'frameStyle',
      'useDefaultFrameWidth',
      'frameWidth',
      'numPanes',
      'glazing',
      'panesAppliedTo',
      'tint',
      'paneThickness',
      'gapType',
    ];
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Skylight',
      uniquePath: true,
      path: () => {
        return `skylights/${this.id}`
      }
    }
  }

  static getTypeMetadata() {
    return {
      name: 'Skylight',
      createType: SkylightType.createType,
      openEditor: SkylightType.openEditor,
      customEditor: true,
    }
  }

  static createType() {
    let type = SkylightType.create(generateItemName('Skylight', gApp.proj().skylightTypes), true)
    gApp.proj().skylightTypes.push(type);
    sortItemsByName(gApp.proj().skylightTypes);
    return type
  }

  static openEditor(skylightType) {
    let pathName = gApp.proj().isCommercial() ? 'building-skylights' : 'skylights';
    gApp.proj().editorState.selectedSkylight = skylightType;
    gApp.router.push({name: pathName});
  }

  static getTableInfo() {
    return {
      typeName: 'Skylight',
      pagePath: 'skylights',
      allowDuplicate: true,
      columns: {
        'name': {
          label: 'Name',
        },
        /*
        'style': {
          label: 'Style',
        },
        */
        height: {
          label: 'Height',
        },
        width: {
          label: 'Width',
        },
        uValue: {
          label: 'U-Value',
        },
        shgc0: {
          label: 'SHGC (0 deg)',
        },
        shgcDiff: {
          label: 'SHGC (Diffuse)',
        },
      }
    }
  }

  getTableData() {
    let shgc0 = "--";
    let shgcDiff = "--";
    if (!this.outputShgc.isWaitingInputs()) {
      let shgcs = this.computeShgc();
      shgc0 = formatNum(shgcs.Deg0);
      shgcDiff = formatNum(shgcs.Diffuse);
    }
    return {
      name: this.name.value,
      //style: SkylightStyle._labels[this.style.value],
      height: this.height.getDisplayValueStr(),
      width: this.width.getDisplayValueStr(),
      uValue: this.outputUValue.getDisplayValueStr(),
      shgc0: shgc0,
      shgcDiff: shgcDiff,
    }
  }

  getComponentTableData() {
    const waiting = this.outputShgc.isWaitingInputs();
    const shgc0 = waiting ? '--' : formatNum(this.computeShgc().Deg0);
    const shgcDiff = waiting ? '--' : formatNum(this.computeShgc().Diffuse);
    return {
      columns: [
        { name: 'Name' },
        { name: 'U-value', units: this.outputUValue.getDisplayUnitsLabel() },
        { name: 'SHGC (0°)' },
        { name: 'SHGC (Diffuse)' },
        { name: 'Height', units: this.height.getDisplayUnitsLabel() },
        { name: 'Width', units: this.width.getDisplayUnitsLabel() },
        { name: 'Curb Height', units: this.curbHeight.getDisplayUnitsLabel() },
      ],
      rows: [[
        { value: this.name.value },
        { value: this.outputUValue.getDisplayValueStr() },
        { value: shgc0 },
        { value: shgcDiff },
        { value: this.height.getDisplayValueStr() },
        { value: this.width.getDisplayValueStr() },
        { value: this.curbHeight.getDisplayValueStr() },
      ]],
    };
  }

  getConstructionDetailsData() {
    const uValueUnits = this.outputUValue.getDisplayUnitsLabel();
    if (this.inputType.value === SkylightInputType.Manual) {
      return {
        columns: [{ name: 'Property' }, { name: 'Value' }],
        rows: [
          [{ value: 'Entry method' }, { value: 'Manual entry' }],
          [{ value: 'U-value', isBold: true }, { value: this.outputUValue.getDisplayValueStr(), units: uValueUnits, isBold: true }],
        ],
        showHeader: false,
      };
    }
    const rows = [[{ value: 'Style' }, { value: this.style.getSelectLabelStr() }]];
    if (this.style.value === SkylightStyle.DomedSkylight) {
      rows.push([{ value: 'Dome opacity' }, { value: this.domedSkylightInputs.getField('domeOpacity').getSelectLabelStr() }]);
      rows.push([{ value: 'Frame style' }, { value: this.domedSkylightInputs.getField('frameStyle').getSelectLabelStr() }]);
      rows.push([{ value: 'Glazing' }, { value: this.domedSkylightInputs.getField('glazing').getSelectLabelStr() }]);
    } else if (this.style.value === SkylightStyle.TubularDaylightingDevice) {
      rows.push([{ value: 'Collector glazing layers' }, { value: this.tubularSkylightInputs.getField('collectorLayers').getSelectLabelStr() }]);
      rows.push([{ value: 'Diffuser glazing layers' }, { value: this.tubularSkylightInputs.getField('diffuserLayers').getSelectLabelStr() }]);
      rows.push([{ value: 'Insulation location' }, { value: this.tubularSkylightInputs.getField('insulationLocation').getSelectLabelStr() }]);
    } else {
      rows.push([{ value: 'Frame style' }, { value: this.frameStyle.getSelectLabelStr() }]);
      rows.push([{ value: 'Number of panes' }, { value: this.numPanes.getSelectLabelStr() }]);
      rows.push([{ value: 'Glazing' }, { value: this.glazing.getSelectLabelStr() }]);
      if (this.numPanes.value !== 'N1' && this.glazing.value !== 'None') {
        rows.push([{ value: 'Panes applied to' }, { value: this.panesAppliedTo.getSelectLabelStr() }]);
      }
      rows.push([{ value: 'Tint / coating' }, { value: this.tint.getSelectLabelStr() }]);
      rows.push([{ value: 'Pane thickness' }, { value: this.paneThickness.getSelectLabelStr() }]);
      rows.push([{ value: 'Gap type' }, { value: this.gapType.getSelectLabelStr() }]);
    }
    rows.push([
      { value: 'U-value', isBold: true },
      { value: this.outputUValue.getDisplayValueStr(), units: uValueUnits, isBold: true },
    ]);
    return {
      columns: [{ name: 'Property' }, { name: 'Value' }],
      rows,
      showHeader: false,
    };
  }

  getInputPage() {
    return {
      label: `${this.name.value}`,
      // TODO
      path: `skylights/${this.id}`,
      typeName: 'Skylight',
      typeObject: this,
    };
  }

  getArea() {
    // This is the area of the glass part
    return this.height.getValueInUnits(Units.ft) * this.width.getValueInUnits(Units.ft);
  }

  getPerimeter() {
    return 2*this.height.getValueInUnits(Units.ft) + 2*this.width.getValueInUnits(Units.ft);
  }

  getCurbArea() {
    // Area of the opaque curb
    let area = this.height.getValueInUnits(Units.ft) + this.width.getValueInUnits(Units.ft);
    let A_FC = 2*this.curbHeight.getValueInUnits(Units.ft)*area;
    return A_FC;
  }

  computeUValue() {
    if (this.inputType.value == SkylightInputType.Manual) {
      return {
        uValue: this.manualUValue.getValueInUnits(Units.UValue),
      }
    } else {
      if (this.style.value == SkylightStyle.DomedSkylight) {
        // NOTE - for domed skylights we override the row lookup for U-value. Otherwise treat
        // the same as other skylights/windows.
        // TODO - at some point refactor and cleanup how we do these data lookups. They are
        // very gross.
        let uValueLookupRow = lookupData(DomedSkylightUValueLookupRows, [
          this.domedSkylightInputs.getField('glazing').value,
        ]);
        // NOTE - we treat the DomedSkylight as a ManufacturedSkylight for the purpose of
        // frame width lookup.
        let frameWidth = lookupData(WindowType._getStdFrameWidthMap(), [
          SkylightStyle.ManufacturedSkylight,
          this.domedSkylightInputs.getField('frameStyle').value,
        ]);
        let inputData = {
          width: this.width.getValueInUnits(Units.ft),
          height: this.height.getValueInUnits(Units.ft),
          frameWidth: Units.convertValue(frameWidth, Units.inches, Units.ft),
          windowStyle: SkylightStyle.ManufacturedSkylight,
          frameStyle: this.domedSkylightInputs.getField('frameStyle').value,
          windowsData: gApp.proj().windowsData,
          isSkylight: true,
          curbHeight: this.curbHeight.getValueInUnits(Units.ft),
          lookupRow: uValueLookupRow,
        }
        return WindowType.computeUValue(inputData);
      } else if (this.style.value == SkylightStyle.TubularDaylightingDevice) {
        let uValue = lookupData(TubularSkylightData, [
          this.tubularSkylightInputs.getField('collectorLayers').value,
          this.tubularSkylightInputs.getField('diffuserLayers').value,
          this.tubularSkylightInputs.getField('insulationLocation').value,
          'uValue',
        ])
        return {
          uValue: uValue,
        }
      } else {
        // Similar method to windows.
        let inputData = {
          width: this.width.getValueInUnits(Units.ft),
          height: this.height.getValueInUnits(Units.ft),
          frameWidth: this.frameWidth.getValueInUnits(Units.ft),
          numPanes: this.numPanes.value,
          glazing: this.glazing.value,
          panesAppliedTo: this.panesAppliedTo.value,
          gapType: this.gapType.value,
          // Note: we pass in the skylight style here. The func handles this fine.
          windowStyle: this.style.value,
          frameStyle: this.frameStyle.value,
          windowsData: gApp.proj().windowsData,
          isSkylight: true,
          curbHeight: this.curbHeight.getValueInUnits(Units.ft),
        }
        return WindowType.computeUValue(inputData);
      }
    }
  }

  computeShgc() {
    if (this.inputType.value == SkylightInputType.Manual) {
      return WindowType._estimateSkylightShgcs(this.manualShgc.value);
    } else {
      if (this.style.value == SkylightStyle.DomedSkylight) {
        let opacity = this.domedSkylightInputs.getField('domeOpacity').value;
        let curbHeight = this.curbHeight.getValueInUnits(Units.inches);
        let interpMap = lookupData(DomedSkylightShgcs, [opacity]);
        let shgc = interpolateInMap(interpMap, curbHeight);
        return WindowType._estimateSkylightShgcs(shgc);
      } else if (this.style.value == SkylightStyle.TubularDaylightingDevice) {
        let shgc = lookupData(TubularSkylightData, [
          this.tubularSkylightInputs.getField('collectorLayers').value,
          this.tubularSkylightInputs.getField('diffuserLayers').value,
          this.tubularSkylightInputs.getField('insulationLocation').value,
          'shgc',
        ])
        return WindowType._estimateSkylightShgcs(shgc);
      } else {
        let inputData = {
          width: this.width.getValueInUnits(Units.ft),
          height: this.height.getValueInUnits(Units.ft),
          frameWidth: this.frameWidth.getValueInUnits(Units.ft),
          numPanes: this.numPanes.value,
          glazing: this.glazing.value,
          panesAppliedTo: this.panesAppliedTo.value,
          gapType: this.gapType.value,
          paneThickness: this.paneThickness.value,
          tint: this.tint.value,
          windowStyle: this.style.value,
          frameStyle: this.frameStyle.value,
          windowsData: gApp.proj().windowsData,
          isSkylight: true,
          curbHeight: this.curbHeight.getValueInUnits(Units.ft),
        }
        return WindowType.computeShgc(inputData);
      }
    }
  }
}
setupClass(SkylightType)
